import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';

import { NextCoachingService } from '../../../coaching-base/coaching.service';
import { ObservationEntity } from '../../../coaching-base/config';
import {
    CoachingTranscript,
    LookupBox,
    TranscriptFocus,
} from '../../../coaching-base/general';
import { TranscriptService as CoachingTranscriptService } from '../../../coaching-base/transcript.service';
import { ObservationService } from '../../../coaching-observation/observation/observation.service';
import { Dependencies } from '../../../store';
import { PayloadAction } from '../../../store.model';
import { TranscriptService } from '../../../teams-call';
import { ApiService, CaResult, CaSummary } from '../../coach-assist';
import { ActionType } from '../transcript-dialog.action';

import { Reload, reloadResult } from './reload.action';

export const reloadEpic = (
    action: ActionsObservable<PayloadAction<ActionType, Reload>>,
    state: StateObservable<any>,
    deps: Dependencies,
): Observable<ActionType> =>
    action.pipe(
        ofType(ActionType.COACHING_DETAIL__TD__RELOAD),
        switchMap((act) => {
            const coachingService =
                deps.injector.get<NextCoachingService>(NextCoachingService);
            const observationService =
                deps.injector.get<ObservationService>(ObservationService);
            const tsService =
                deps.injector.get<TranscriptService>(TranscriptService);
            const ctsService = deps.injector.get<CoachingTranscriptService>(
                CoachingTranscriptService,
            );
            const caService = deps.injector.get<ApiService>(ApiService);

            return forkJoin([
                of(null).pipe(coachingService.retrieveLookupBox()),
                of(null).pipe(observationService.retrieveObservationConfig()),
                tsService.retrieve(act.payload.transcriptId),
                ctsService.listFocuses(act.payload.transcriptId),
                caService.retrieveCaSummary(act.payload.transcriptId),
            ]).pipe(
                concatMap((ret) => {
                    return caService.retrieveCaResult(ret[2].meetingId).pipe(
                        map((ret5) => {
                            return [...ret, ret5];
                        }),
                    );
                }),
                map(
                    (
                        ret: [
                            LookupBox,
                            ObservationEntity,
                            CoachingTranscript,
                            TranscriptFocus[],
                            CaSummary,
                            CaResult,
                        ],
                    ) => {
                        ret[2].focuses = ret[3];
                        ret[2].summary = ret[4]?.content;
                        ret[2].caResult = ret[5];
                        return [ret[0], ret[1], ret[2]];
                    },
                ),
                map(
                    (
                        ret: [LookupBox, ObservationEntity, CoachingTranscript],
                    ) => {
                        return reloadResult(
                            {
                                ...act.payload,
                                behaviours: ret[0].behaviours,
                                subBehaviours: ret[0].subBehaviours,
                                observationConfig: ret[1],
                                transcript: ret[2],
                            },
                            act.storePath,
                        );
                    },
                ),
                catchError((err) => {
                    console.log(err);
                    return of(null);
                }),
            );
        }),
    );
